import {
  Box,
  Button,
  ButtonGroup,
  DialogContent,
  Table,
  TableCell,
  TableRow,
  TextField,
  styled,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { StripePaymentDialog } from "../dialogs/StripePaymentDialog";
import { registerOrder } from "../apis/RangerOnlineApi";
import { useNavigate } from "react-router-dom";
import { PayPalBox } from "./PayPalBox";
import { MonerisPaymentBox } from "./MonerisPaymentBox";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: "#972020",
}));

const PaySummary = ({ order, taxes }) => {
  return (
    <Table>
      <TableRow>
        <StyledTableCell colSpan={2}>Subtotal:</StyledTableCell>
        <StyledTableCell align="right">
          {`$${(order?.subTotal || 0).toFixed(2)}`}
        </StyledTableCell>
        <StyledTableCell colSpan={1}></StyledTableCell>
        {order?.OrderDetails?.length > 0 ? <TableCell rowSpan={5} /> : null}
      </TableRow>
      
      <TableRow>
        <StyledTableCell colSpan={2}>Tax:</StyledTableCell>
        <StyledTableCell align="right">
          {`$${(order.totalTax1+order.totalTax2+order.totalTax3+order.totalTax4+order.totalTax5 || 0).toFixed(2)}`}
        </StyledTableCell>
        <StyledTableCell colSpan={1}></StyledTableCell>
      </TableRow>

      {order?.serviceCharge > 0 ? (
        <TableRow>
          <StyledTableCell colSpan={2}>Service Charge:</StyledTableCell>
          <StyledTableCell align="right">
            {`$${(order.serviceCharge || 0).toFixed(2)}`}
          </StyledTableCell>
          <StyledTableCell colSpan={1}></StyledTableCell>
        </TableRow>
      ) : null}
      {order?.deliveryCharge > 0 ? (
        <TableRow>
          <StyledTableCell colSpan={2}>Delivery Charge:</StyledTableCell>
          <StyledTableCell align="right">
            {`$${(order.deliveryCharge || 0).toFixed(2)}`}
          </StyledTableCell>
          <StyledTableCell colSpan={1}></StyledTableCell>
        </TableRow>
      ) : null}

      {order?.tip > 0 ? (
        <TableRow>
          <StyledTableCell colSpan={2}>Tip:</StyledTableCell>
          <StyledTableCell align="right">
            {`$${(order.tip || 0).toFixed(2)}`}
          </StyledTableCell>
          <StyledTableCell colSpan={1}></StyledTableCell>
        </TableRow>
      ) : null}

      <TableRow>
        <StyledTableCell colSpan={2}>Total:</StyledTableCell>
        <StyledTableCell align="right">
          {`$${(order.orderTotal || 0).toFixed(2)}`}
        </StyledTableCell>
        <StyledTableCell colSpan={1}></StyledTableCell>
      </TableRow>
    </Table>
  );
};

export const PaymentBox = (props) => {
  let navigate = useNavigate();
  const {
    paymentProvider,
    order,
    notes,
    onTipChange,
    taxes,
    settings,
    orderType,
    companyData,
  } = props;
  const {
    DontPayAtStoreDelivery,
    DontPayAtStorePickup,
    EnableGiftcardPromo,
    GlobalDelivery,
    GlobalPickup,
    MonerisDelivery,
    MonerisPickup,
    StripeDelivery,
    StripePickup,
    TDDelivery,
    PaypalDelivery,
    PaypalPickup,
    TDPickup,
    CloverPickup,
    CloverDelivery,
    DontDisplayTipOption,
    DisplayLocationOnCheckout,
  } = settings;
  const { companyName, address1, address2 } = companyData;
  const [loader, setLoader] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const _renderCheckOutDialog = (paymentProvider) => {
    switch (paymentProvider) {
      case "paypal":
        return (
          <PayPalBox
            open={paymentOpen}
            showLoader={loader}
            order={order}
            onClose={() => setPaymentOpen(false)}
          />
        );
      case "stripe":
        return (
          <StripePaymentDialog
            open={paymentOpen}
            onClose={() => setPaymentOpen(false)}
            order={order}
          />
        );
      case "moneris":
      case "clover":
      case "global":
        return (
          <MonerisPaymentBox order={order} paymentProvider={paymentProvider} />
        );
      default:
        return null;
    }
  };

  const handlePayAtCounter = async () => {
    let finalOrder = { ...order };
    finalOrder.onDate = `${
      finalOrder.date.getMonth() + 1
    }/${finalOrder.date.getDate()}/${finalOrder.date.getFullYear()}`;

    finalOrder.onTime = `${finalOrder.time
      .getHours()
      .toString()
      .padStart(2, "0")}:${finalOrder.time
      .getMinutes()
      .toString()
      .padStart(2, "0")}:${finalOrder.time
      .getSeconds()
      .toString()
      .padStart(2, "0")}`;
    finalOrder.notes = notes;

    const orderResponse = await registerOrder(finalOrder);

    if (orderResponse && orderResponse?.responseCode === "0000") {
      navigate(`/Success/${orderResponse.orderId}`);
    }
  };

  return (
    <Box>
      <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
        <PaySummary order={order} taxes={taxes} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
            overflowX: "hidden",
          }}
        >
          {DontDisplayTipOption !== "1" ? (
            <ButtonGroup
              variant="contained"
              sx={{ paddingTop: "5px", marginBottom: "5px" }}
            >
              <Button
                variant="outlined"
                onClick={() => onTipChange(10, "percentage")}
              >
                10%
              </Button>
              <Button
                variant="outlined"
                onClick={() => onTipChange(15, "percentage")}
              >
                15%
              </Button>
              <Button
                variant="outlined"
                onClick={() => onTipChange(20, "percentage")}
              >
                20%
              </Button>
              <TextField
                id="tip"
                type="number"
                label="Tip $:"
                variant="outlined"
                sx={{ width: "60px" }}
                inputProps={{
                  pattern: "d+(.d*)?",
                  inputMode: "numeric",
                  style: { textAlign: "center" },
                }}
                onChange={(e) => onTipChange(e, "amount")}
                value={order.tip}
              />
            </ButtonGroup>
          ) : null}
          {DisplayLocationOnCheckout === 1 ? (
            <Typography sx={{ paddingTop: "5px", marginBottom: "5px" }}>
              {`Your order is being placed to ${companyName},${address1} ${address2}`}
            </Typography>
          ) : null}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              marginTop: "50px",
            }}
          >
            {((orderType === "Delivery" && DontPayAtStoreDelivery === "0") ||
              (orderType === "TakeOut" && DontPayAtStorePickup === "0")) && (
              <Button
                variant="contained"
                color={"primary"}
                sx={{ width: "150px", margin: 1, fontSize: "0.875rem" }}
                onClick={handlePayAtCounter}
              >
                Pay at counter
              </Button>
            )}
            {(EnableGiftcardPromo == 1 ||
              GlobalDelivery == 1 ||
              GlobalPickup == 1 ||
              MonerisDelivery == 1 ||
              MonerisPickup == 1 ||
              StripeDelivery == 1 ||
              StripePickup == 1 ||
              TDDelivery == 1 ||
              PaypalDelivery == 1 ||
              PaypalPickup == 1 ||
              TDPickup == 1 ||
              CloverPickup == 1 ||
              CloverDelivery == 1) && (
              <Button
                variant="contained"
                color={"primary"}
                sx={{ width: "150px", margin: 1, fontSize: "0.875rem" }}
                onClick={() => setPaymentOpen(true)}
              >
                Pay now
              </Button>
            )}
          </Box>
        </Box>
        {paymentOpen ? _renderCheckOutDialog(paymentProvider) : null}
      </DialogContent>
    </Box>
  );
};
